// Internal helper (not exported): derive busy state from provided query / mutation objects

import { CustomUseMutationResult, CustomUseQueryResult } from "@repo/logic";
import { QuestionnaireQueryType } from "../../PensionQuestionnaire";

// Keeps component body lean and allows potential reuse / unit testing.
type QueryLike =
    | CustomUseQueryResult<unknown, unknown>
    | CustomUseMutationResult<unknown, unknown, unknown>
    | Record<string, unknown>;

interface QueryDiagEntry {
    key: string;
    invalid: boolean;
    isMutation: boolean;
    isPending: boolean;
    isLoading: boolean;
    isFetching: boolean;
    isRefetching: boolean;
    busy: boolean;
}

export const computeExternalBusy = (queries?: QuestionnaireQueryType["queries"]): boolean => {
    if (!queries) return false;
    const diagEntries: QueryDiagEntry[] = (Object.keys(queries) as (keyof typeof queries)[]).map((key) => {
        const q = queries[key] as QueryLike | undefined;
        if (!q || typeof q !== "object") {
            return {
                key: String(key),
                invalid: true,
                isMutation: false,
                isPending: false,
                isLoading: false,
                isFetching: false,
                isRefetching: false,
                busy: false,
            };
        }
        const maybeMutation = q as Partial<CustomUseMutationResult<unknown, unknown, unknown>> & Record<string, any>;
        const maybeQuery = q as Partial<CustomUseQueryResult<unknown, unknown>> & Record<string, any>;
        const isMutation =
            typeof maybeMutation.mutate === "function" || typeof maybeMutation.mutateAsync === "function";
        const isPending = (maybeMutation.isPending ?? false) === true; // mutation state
        const isLoading = (maybeQuery.isLoading ?? false) === true;
        const isFetching = (maybeQuery.isFetching ?? false) === true;
        const isRefetching = (maybeQuery.isRefetching ?? false) === true;
        const busy = (isMutation && isPending) || (!isMutation && (isLoading || isFetching || isRefetching));
        return {
            key: String(key),
            invalid: false,
            isMutation,
            isPending,
            isLoading,
            isFetching,
            isRefetching,
            busy,
        };
    });
    return diagEntries.filter((d) => !d.invalid).some((d) => d.busy);
};
